import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { FiX } from 'react-icons/fi';
import ChartFillDetails from './ChartFillDetails';
import {
  Area,
  Bar,
  CartesianGrid,
  Cell,
  ComposedChart,
  Line,
  ReferenceArea,
  ReferenceDot,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import type {
  ChartAxis,
  ChartMarker,
  ChartPane,
  ChartRow,
  ChartSeries,
  ChartSpec,
  ChartStyle,
  ChartValueFormat,
} from './types';
import {
  FALLBACK_PLOT_WIDTH_PX,
  bucketPlotRows,
  displayedRowForIndex,
  seriesAggregations,
  shouldRenderCandlesticks,
  targetDisplayPointCount,
} from './displayDensity';
import { chartTimeMilliseconds, chartTimeRange, formatChartTime } from './timeRange';
import { chartAreas, chartObjectLayerId } from './layers';
import { clusterVisibleMarkers, createMarkerPositioner } from './markerDensity';
import { candlePixelGeometry, positiveLogDomain } from './scaleModel';
import { useThemeMode } from '../../design-system';

// SAFETY: react-icons renders an SVG and accepts the standard SVG prop contract.
const CloseIcon = FiX as React.ComponentType<React.SVGProps<SVGSVGElement>>;

const DEFAULT_COLORS = ['#60A5FA', '#A78BFA', '#22C55E', '#F59E0B', '#F472B6', '#22D3EE'];
const TONE_BACKGROUNDS = {
  primary: '#0b1220',
  indicator: '#0d1525',
  volume: '#0a1420',
  comparison: '#0d1422',
  neutral: '#0b1220',
};
const LIGHT_TONE_BACKGROUNDS = {
  primary: '#ffffff',
  indicator: '#f8fafc',
  volume: '#f1f5f9',
  comparison: '#f8fafc',
  neutral: '#ffffff',
};
const COLOR_TOKENS = {
  dark: {
    price: '#60A5FA',
    volume: '#38BDF8',
    indicator: '#F59E0B',
    comparison: '#A78BFA',
    positive: '#22C55E',
    negative: '#EF4444',
    neutral: '#94A3B8',
  },
  light: {
    price: '#1D4ED8',
    volume: '#0369A1',
    indicator: '#B45309',
    comparison: '#6D28D9',
    positive: '#15803D',
    negative: '#B91C1C',
    neutral: '#475569',
  },
} as const;

export function resolveChartColor(
  style: ChartStyle | undefined,
  index: number,
  theme: 'dark' | 'light',
): string {
  if (style?.colorToken) return COLOR_TOKENS[theme][style.colorToken];
  const explicit = style?.color?.trim();
  if (explicit) {
    const normalized = explicit.toLowerCase();
    if (theme === 'dark' && ['#000', '#000000', 'black', 'rgb(0, 0, 0)'].includes(normalized)) {
      return COLOR_TOKENS.dark.neutral;
    }
    if (
      theme === 'light' &&
      ['#fff', '#ffffff', 'white', 'rgb(255, 255, 255)'].includes(normalized)
    ) {
      return COLOR_TOKENS.light.neutral;
    }
    return explicit;
  }
  return theme === 'dark'
    ? DEFAULT_COLORS[index % DEFAULT_COLORS.length]
    : ['#1D4ED8', '#6D28D9', '#15803D', '#B45309', '#BE185D', '#0E7490'][
        index % DEFAULT_COLORS.length
      ];
}

export function referenceLabelOffsets(values: number[], collisionThreshold: number): number[] {
  const offsets = Array(values.length).fill(0);
  const ordered = values
    .map((value, index) => ({ value, index }))
    .sort((a, b) => a.value - b.value);
  let start = 0;
  while (start < ordered.length) {
    let end = start + 1;
    while (
      end < ordered.length &&
      ordered[end].value - ordered[end - 1].value <= collisionThreshold
    )
      end += 1;
    const cluster = ordered.slice(start, end).sort((a, b) => b.value - a.value);
    if (cluster.length > 1) {
      const pixelsPerValue = 42 / Math.max(collisionThreshold, Number.EPSILON);
      const rawPositions = cluster.map((item) => -item.value * pixelsPerValue);
      const placedPositions = rawPositions.slice();
      for (let index = 1; index < placedPositions.length; index += 1) {
        placedPositions[index] = Math.max(rawPositions[index], placedPositions[index - 1] + 20);
      }
      const centeringShift =
        rawPositions.reduce((sum, value) => sum + value, 0) / rawPositions.length -
        placedPositions.reduce((sum, value) => sum + value, 0) / placedPositions.length;
      cluster.forEach((item, clusterIndex) => {
        offsets[item.index] = Math.round(
          placedPositions[clusterIndex] + centeringShift - rawPositions[clusterIndex],
        );
      });
    }
    start = end;
  }
  return offsets;
}

type PlotRow = ChartRow & { __chartX: number; __chartIndex: number };

export function chartCoordinate(spec: ChartSpec, value: unknown, index: number): number {
  if (spec.xAxis.scaleMode === 'ordinal' || spec.xAxis.scaleMode === 'session') return index;
  if (spec.xAxis.type === 'number') return Number(value);
  if (spec.xAxis.type === 'time') return chartTimeMilliseconds(value) ?? Number.NaN;
  return index;
}

function plottedRows(spec: ChartSpec): PlotRow[] {
  return spec.data.map((row, index) => ({
    ...row,
    __chartX: chartCoordinate(spec, row[spec.xAxis.key], index),
    __chartIndex: index,
  }));
}

function formatNumber(value: unknown, format: ChartAxis['format']): string {
  const number = Number(value);
  if (!Number.isFinite(number)) return '';
  if (format === 'currency') {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 2,
    }).format(number);
  }
  if (format === 'percent') return `${number.toFixed(2)}%`;
  if (format === 'compact') {
    return new Intl.NumberFormat(undefined, {
      notation: 'compact',
      maximumFractionDigits: 1,
    }).format(number);
  }
  if (format === 'integer') return Math.round(number).toLocaleString();
  return number.toLocaleString(undefined, { maximumFractionDigits: 4 });
}

function formatHoverValue(value: unknown, format: ChartValueFormat | 'date' | 'text'): string {
  if (value === null || value === undefined || value === '') return '';
  if (format === 'text') return String(value);
  if (format === 'date') {
    const date = new Date(String(value));
    return Number.isNaN(date.getTime())
      ? String(value)
      : date.toLocaleDateString(undefined, {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
        });
  }
  return formatNumber(value, format);
}

function paneDisplayLabel(pane: ChartPane): string {
  if (pane.label) return pane.label;
  const cleaned = pane.id
    .replace(/[_-]+/g, ' ')
    .replace(/\bpane\b/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  if (!cleaned) return 'Chart';
  return cleaned.replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function formatX(
  spec: ChartSpec,
  row: PlotRow | undefined,
  range: ReturnType<typeof chartTimeRange>,
  detail = false,
): string {
  if (!row) return '';
  const value = row[spec.xAxis.key];
  if (spec.xAxis.type !== 'time') return String(value ?? '');
  return formatChartTime(value, range, spec.xAxis.timezone, detail);
}

function dashArray(dash: ChartStyle['dash']): string | undefined {
  if (dash === 'dashed') return '7 5';
  if (dash === 'dotted') return '2 4';
  return undefined;
}

function lineType(
  interpolation: ChartSeries['interpolation'],
): 'linear' | 'stepAfter' | 'monotone' {
  if (interpolation === 'step') return 'stepAfter';
  if (interpolation === 'monotone') return 'monotone';
  return 'linear';
}

function seriesValue(row: ChartRow | undefined, series: ChartSeries): number | null {
  if (!row) return null;
  const key = series.mark === 'candlestick' ? series.fieldMapping?.close : series.dataKey;
  const rawValue = key ? row[key] : undefined;
  if (rawValue === null || rawValue === undefined || rawValue === '') return null;
  const value = Number(rawValue);
  return Number.isFinite(value) ? value : null;
}

function barColor(series: ChartSeries, row: ChartRow, previousRow?: ChartRow): string {
  const mapping = series.barColors;
  if (!mapping) return series.style?.color || series.style?.fill || '#38BDF8';
  if (mapping.mode === 'field') {
    const value = String(mapping.colorKey ? (row[mapping.colorKey] ?? '') : '').toLowerCase();
    if (['up', 'positive', 'green', 'true', '1'].includes(value)) return mapping.positiveColor;
    if (['down', 'negative', 'red', 'false', '-1'].includes(value)) return mapping.negativeColor;
    return mapping.neutralColor || '#64748B';
  }
  const close = Number(mapping.closeKey ? row[mapping.closeKey] : Number.NaN);
  const comparison = Number(
    mapping.openKey
      ? row[mapping.openKey]
      : mapping.comparisonKey
        ? previousRow?.[mapping.comparisonKey]
        : previousRow?.[mapping.closeKey || ''],
  );
  if (!Number.isFinite(close) || !Number.isFinite(comparison))
    return mapping.neutralColor || '#64748B';
  if (close > comparison) return mapping.positiveColor;
  if (close < comparison) return mapping.negativeColor;
  return mapping.neutralColor || '#64748B';
}

function CandlestickShape(props: any): React.ReactElement | null {
  const { x, y, width, height, payload, series, yAxis } = props;
  const mapping = series.fieldMapping;
  const open = Number(payload[mapping.open]);
  const high = Number(payload[mapping.high]);
  const low = Number(payload[mapping.low]);
  const close = Number(payload[mapping.close]);
  if (![x, y, width, height, open, high, low, close].every(Number.isFinite)) return null;
  const scale =
    typeof yAxis?.scale === 'function'
      ? (value: number) => yAxis.scale(value)
      : (value: number) => y + ((high - value) / Math.max(high - low, Number.EPSILON)) * height;
  const geometry = candlePixelGeometry({ open, high, low, close }, scale);
  const color =
    close >= open
      ? series.style?.positiveColor || '#22C55E'
      : series.style?.negativeColor || '#EF4444';
  const center = x + width / 2;
  const bodyWidth = Math.max(3, width * 0.62);
  return (
    <g
      data-series-mark="candlestick"
      data-series-id={series.id}
      data-semantic-element-id={series.semanticElementId}
    >
      <line
        x1={center}
        x2={center}
        y1={geometry.wickTop}
        y2={geometry.wickBottom}
        stroke={color}
        strokeWidth={1}
      />
      <rect
        x={center - bodyWidth / 2}
        y={geometry.bodyTop}
        width={bodyWidth}
        height={Math.max(geometry.bodyBottom - geometry.bodyTop, 2)}
        rx={0.75}
        fill={color}
      />
    </g>
  );
}

function formatTradeMarkerTime(value: string | number): string {
  const milliseconds = chartTimeMilliseconds(value);
  if (milliseconds === null) return String(value);
  return new Date(milliseconds).toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function formatTradeMarkerNumber(
  value: number | undefined,
  kind: 'quantity' | 'price',
): string | null {
  if (!Number.isFinite(Number(value))) return null;
  if (kind === 'price') {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 8,
    }).format(Number(value));
  }
  return Number(value).toLocaleString(undefined, { maximumFractionDigits: 10 });
}

function formatTradeDetailLabel(value: string | undefined): string | null {
  const label = String(value || '')
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  if (!label) return null;
  return label.toLowerCase().replace(/^\w/, (letter) => letter.toUpperCase());
}

function formatTradeExpiration(value: string | undefined): string | null {
  if (!value) return null;
  const date = new Date(`${value.slice(0, 10)}T12:00:00.000Z`);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  });
}

function formatTradePosition(value: number | undefined): string | null {
  if (!Number.isFinite(Number(value))) return null;
  const quantity = Math.abs(Number(value)).toLocaleString(undefined, { maximumFractionDigits: 10 });
  if (Number(value) === 0) return 'Flat';
  return `${Number(value) > 0 ? 'Long' : 'Short'} ${quantity}`;
}

function TradeDetailRow({
  label,
  value,
}: {
  label: string;
  value: React.ReactNode;
}): React.ReactElement | null {
  if (value === null || value === undefined || value === '') return null;
  return (
    <div>
      <dt>{label}</dt>
      <dd>{value}</dd>
    </div>
  );
}

function MarkerGlyph({
  cx: anchorX,
  cy,
  marker,
  compact = false,
  labelPolicy,
}: any): React.ReactElement {
  const [theme] = useThemeMode();
  const cx = anchorX + (marker.displayOffsetXPx || 0);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [detailsPinned, setDetailsPinned] = useState(false);

  const clusterMembers: ChartMarker[] = marker.clusterMembers || [];
  const anchorElement = useRef<SVGElement | null>(null);
  const tooltipElement = useRef<HTMLDivElement | null>(null);
  useLayoutEffect(() => {
    if (!detailsOpen || clusterMembers.length <= 1) return;
    const updatePosition = () => {
      const anchor = anchorElement.current;
      const tooltip = tooltipElement.current;
      if (!anchor || !tooltip) return;
      const bounds = anchor.getBoundingClientRect();
      const popup = tooltip.getBoundingClientRect();
      const paneBounds = anchor.closest('.unified-chart__pane')?.getBoundingClientRect();
      const mobile = window.innerWidth <= 419;
      const left = mobile
        ? Math.max(8, paneBounds?.left ?? 8)
        : Math.max(
            8,
            Math.min(
              window.innerWidth - popup.width - 8,
              bounds.left > popup.width + 24 ? bounds.left - popup.width - 10 : bounds.right + 10,
            ),
          );
      const top = mobile
        ? Math.max(
            8,
            Math.min(paneBounds?.bottom ?? window.innerHeight, window.innerHeight - 8) -
              popup.height,
          )
        : Math.max(
            paneBounds && paneBounds.height >= popup.height + 16 ? paneBounds.top + 8 : 8,
            Math.min(
              (paneBounds && paneBounds.height >= popup.height + 16
                ? Math.min(paneBounds.bottom, window.innerHeight)
                : window.innerHeight) -
                popup.height -
                8,
              bounds.top + bounds.height / 2 - popup.height / 2,
            ),
          );
      setTooltipPosition((current) =>
        current?.left === left && current?.top === top ? current : { left, top },
      );
    };
    updatePosition();
    const observer = new ResizeObserver(updatePosition);
    if (tooltipElement.current) observer.observe(tooltipElement.current);
    window.addEventListener('resize', updatePosition);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', updatePosition);
    };
  }, [detailsOpen, clusterMembers.length]);
  useEffect(() => {
    if (!detailsOpen || !detailsPinned) return;
    const dismissOutside = (event: PointerEvent) => {
      if (!(event.target instanceof Node)) return;
      if (
        tooltipElement.current?.contains(event.target) ||
        anchorElement.current?.contains(event.target)
      )
        return;
      setDetailsPinned(false);
      setDetailsOpen(false);
    };
    document.addEventListener('pointerdown', dismissOutside);
    return () => document.removeEventListener('pointerdown', dismissOutside);
  }, [detailsOpen, detailsPinned]);

  useEffect(() => {
    if (!detailsOpen) return;
    const dismiss = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      setDetailsPinned(false);
      setDetailsOpen(false);
    };
    document.addEventListener('keydown', dismiss);
    return () => document.removeEventListener('keydown', dismiss);
  }, [detailsOpen]);
  const [tooltipPosition, setTooltipPosition] = useState<{ left: number; top: number } | null>(
    null,
  );
  const offsetY = marker.placement === 'above' ? -10 : marker.placement === 'below' ? 10 : 0;
  const markerY = cy + offsetY;
  const labelY = markerY + (marker.placement === 'below' ? 29 : -18);
  const width = Math.max(42, String(marker.label).length * 6.5 + 14);
  const details = marker.details || {};
  const quantity = formatTradeMarkerNumber(details.quantity, 'quantity');
  const price = formatTradeMarkerNumber(details.price, 'price');
  const strike = formatTradeMarkerNumber(details.strike, 'price');
  const tradeValue = formatTradeMarkerNumber(details.tradeValue, 'price');
  const assetType = formatTradeDetailLabel(details.assetType);
  const optionType = formatTradeDetailLabel(details.optionType);
  const expiration = formatTradeExpiration(details.expiration);
  const orderType = formatTradeDetailLabel(details.orderType);
  const positionBefore = formatTradePosition(details.positionBefore);
  const positionAfter = formatTradePosition(details.positionAfter);
  const openingQuantity = formatTradeMarkerNumber(details.openingQuantity, 'quantity');
  const closingQuantity = formatTradeMarkerNumber(details.closingQuantity, 'quantity');
  const accessibleLabel =
    clusterMembers.length > 1
      ? `${marker.label} ${marker.side || 'events'} ${details.symbol || ''}`
      : marker.side
        ? [
            details.description || marker.label,
            details.description ? null : quantity,
            details.description ? null : details.symbol,
            details.description ? null : price ? `at ${price}` : null,
            details.status,
          ]
            .filter(Boolean)
            .join(' ')
        : marker.detail || marker.label;
  const glyph =
    marker.shape === 'arrowUp' ? (
      <path d="M 0 -10 L 8 1 L 4 1 L 4 9 L -4 9 L -4 1 L -8 1 Z" fill={marker.color} />
    ) : marker.shape === 'arrowDown' ? (
      <path d="M 0 10 L 8 -1 L 4 -1 L 4 -9 L -4 -9 L -4 -1 L -8 -1 Z" fill={marker.color} />
    ) : marker.shape === 'square' ? (
      <rect x={-6} y={-6} width={12} height={12} rx={2} fill={marker.color} />
    ) : (
      <circle cx={0} cy={0} r={6} fill={marker.color} />
    );
  const openDetails = (target: EventTarget | null) => {
    if (!(target instanceof SVGElement)) return;
    anchorElement.current = target;
    const bounds = target.getBoundingClientRect();
    const grouped = clusterMembers.length > 1;
    const tooltipWidth = grouped ? Math.min(360, window.innerWidth - 16) : 320;
    const tooltipHeight = grouped ? 440 : 520;
    const paneBounds = target.closest('.unified-chart__pane')?.getBoundingClientRect();
    const left =
      bounds.left < tooltipWidth + 24 ? bounds.right + 10 : bounds.left - tooltipWidth - 10;
    const top =
      bounds.top < tooltipHeight + 24 ? bounds.bottom + 10 : bounds.top - tooltipHeight - 10;
    if (grouped && window.innerWidth <= 419 && paneBounds) {
      setTooltipPosition({
        left: Math.max(8, paneBounds.left),
        top: Math.max(
          8,
          Math.min(paneBounds.bottom, window.innerHeight - 8) -
            Math.min(tooltipHeight, window.innerHeight * 0.6),
        ),
      });
      setDetailsOpen(true);
      return;
    }
    setTooltipPosition({
      left: Math.max(8, Math.min(window.innerWidth - tooltipWidth - 8, left)),
      top: Math.max(8, Math.min(window.innerHeight - tooltipHeight - 8, top)),
    });
    setDetailsOpen(true);
  };
  return (
    <>
      <g
        data-chart-marker={marker.id}
        data-chart-marker-label={marker.label}
        data-chart-marker-shape={marker.shape}
        data-chart-marker-action={marker.action}
        data-chart-marker-count={clusterMembers.length || 1}
        data-marker-visual-size="emphasized"
        data-semantic-element-id={marker.semanticElementId}
        role="button"
        aria-label={accessibleLabel}
        aria-expanded={detailsOpen}
        tabIndex={0}
        onMouseEnter={(event) => openDetails(event.currentTarget)}
        onMouseLeave={() => {
          if (!detailsPinned) setDetailsOpen(false);
        }}
        onFocus={(event) => openDetails(event.currentTarget)}
        onBlur={() => {
          if (!detailsPinned) setDetailsOpen(false);
        }}
        onClick={(event) => {
          if (detailsPinned) {
            setDetailsPinned(false);
            setDetailsOpen(false);
          } else {
            openDetails(event.currentTarget);
            setDetailsPinned(true);
          }
        }}
        onKeyDown={(event) => {
          if (event.key === 'Escape') {
            setDetailsPinned(false);
            setDetailsOpen(false);
          }
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            if (detailsPinned) {
              setDetailsPinned(false);
              setDetailsOpen(false);
            } else {
              openDetails(event.currentTarget);
              setDetailsPinned(true);
            }
          }
        }}
      >
        <title>{accessibleLabel}</title>
        {marker.displayOffsetXPx ? (
          <path
            d={`M ${anchorX} ${cy} L ${cx} ${markerY}`}
            stroke={marker.color}
            strokeWidth={1}
            strokeOpacity={0.5}
            fill="none"
            pointerEvents="none"
          />
        ) : null}
        <circle
          cx={cx}
          cy={markerY}
          r={14}
          fill="transparent"
          pointerEvents="all"
          data-chart-marker-hit-target={marker.id}
        />
        <g
          transform={`translate(${cx} ${markerY})`}
          stroke={theme === 'light' ? '#FFFFFF' : '#08111F'}
          strokeWidth={1.5}
          strokeLinejoin="round"
        >
          {glyph}
        </g>
        {clusterMembers.length > 1 && (
          <text x={cx + 12} y={markerY + 4} fill={marker.color} fontSize={11} fontWeight={700}>
            {clusterMembers.length}
          </text>
        )}
        {!marker.side && labelPolicy !== 'hidden' && (
          <g
            data-chart-marker-label-policy={labelPolicy}
            className={
              compact
                ? 'unified-chart__marker-label unified-chart__marker-label--compact'
                : 'unified-chart__marker-label'
            }
          >
            <rect
              x={cx + 7}
              y={labelY - 13}
              width={width}
              height={18}
              rx={5}
              fill={theme === 'light' ? '#FFFFFF' : '#08111F'}
              stroke={marker.color}
              data-testid={`chart-label-background-${marker.id}`}
            />
            <text
              x={cx + 14}
              y={labelY}
              fill={theme === 'light' ? '#0F172A' : '#F8FAFC'}
              fontSize={11}
              fontWeight={700}
            >
              {marker.label}
            </text>
          </g>
        )}
      </g>
      {detailsOpen &&
        tooltipPosition &&
        createPortal(
          <div
            ref={tooltipElement}
            className={
              clusterMembers.length > 1
                ? 'unified-chart__trade-tooltip unified-chart__trade-tooltip--fills'
                : 'unified-chart__trade-tooltip'
            }
            data-chart-theme={theme}
            data-details-pinned={detailsPinned}
            role={clusterMembers.length > 1 && detailsPinned ? 'dialog' : 'tooltip'}
            aria-label={marker.side ? 'Trade details' : 'Chart event details'}
            style={{ left: tooltipPosition.left, top: tooltipPosition.top }}
          >
            <div className="unified-chart__trade-tooltip-heading">
              <strong>
                {clusterMembers.length > 1 ? (
                  <>
                    {`${details.symbol || 'Trades'} · ${formatTradeDetailLabel(marker.action || marker.side) || 'Fills'}`}
                    <small className="chart-fill-details__count">
                      {clusterMembers.length.toLocaleString()} fills
                    </small>
                  </>
                ) : (
                  details.actionLabel || marker.label
                )}
              </strong>
              {clusterMembers.length > 1 && (
                <button
                  className="chart-fill-details__close"
                  type="button"
                  aria-label="Close trade details"
                  onClick={() => {
                    setDetailsPinned(false);
                    setDetailsOpen(false);
                  }}
                >
                  <CloseIcon aria-hidden="true" />
                </button>
              )}
              {details.status && <span>{details.status}</span>}
            </div>
            {clusterMembers.length > 1 ? (
              <>
                {!detailsPinned && (
                  <p className="chart-fill-details__hint">Click marker to inspect</p>
                )}
                {detailsPinned && (
                  <ChartFillDetails
                    fills={clusterMembers}
                    formatTime={formatTradeMarkerTime}
                    formatNumber={formatTradeMarkerNumber}
                  />
                )}
              </>
            ) : marker.side ? (
              <section className="unified-chart__trade-tooltip-section">
                <h5>Execution</h5>
                <dl>
                  <TradeDetailRow
                    label="Filled at"
                    value={formatTradeMarkerTime(details.filledAt || marker.x)}
                  />
                  <TradeDetailRow label="Symbol" value={details.symbol} />
                  <TradeDetailRow label="Quantity" value={quantity} />
                  <TradeDetailRow label="Fill price" value={price} />
                  <TradeDetailRow label="Trade value" value={tradeValue} />
                </dl>
              </section>
            ) : (
              <section className="unified-chart__trade-tooltip-section">
                <h5>Signal</h5>
                <dl>
                  <TradeDetailRow label="Observed" value={formatTradeMarkerTime(marker.x)} />
                  <TradeDetailRow
                    label="Value"
                    value={
                      Number.isFinite(Number(marker.value))
                        ? Number(marker.value).toLocaleString(undefined, {
                            maximumFractionDigits: 8,
                          })
                        : null
                    }
                  />
                </dl>
                {marker.detail && <p>{marker.detail}</p>}
              </section>
            )}
            {clusterMembers.length <= 1 &&
              marker.side &&
              (assetType || optionType || strike || expiration || details.multiplier) && (
                <section className="unified-chart__trade-tooltip-section">
                  <h5>Contract</h5>
                  <dl>
                    <TradeDetailRow label="Asset" value={assetType} />
                    <TradeDetailRow label="Option type" value={optionType} />
                    <TradeDetailRow label="Strike" value={strike} />
                    <TradeDetailRow label="Expiration" value={expiration} />
                    <TradeDetailRow label="Multiplier" value={details.multiplier} />
                  </dl>
                </section>
              )}
            {clusterMembers.length <= 1 &&
              marker.side &&
              (orderType || details.timeInForce || details.strategy) && (
                <section className="unified-chart__trade-tooltip-section">
                  <h5>Order</h5>
                  <dl>
                    <TradeDetailRow label="Order type" value={orderType} />
                    <TradeDetailRow label="Time in force" value={details.timeInForce} />
                    <TradeDetailRow label="Strategy" value={details.strategy} />
                  </dl>
                </section>
              )}
            {clusterMembers.length <= 1 &&
              marker.side &&
              (positionBefore || positionAfter || openingQuantity || closingQuantity) && (
                <section className="unified-chart__trade-tooltip-section">
                  <h5>Position</h5>
                  <dl>
                    <TradeDetailRow label="Before" value={positionBefore} />
                    <TradeDetailRow label="After" value={positionAfter} />
                    <TradeDetailRow label="Closed" value={closingQuantity} />
                    <TradeDetailRow label="Opened" value={openingQuantity} />
                  </dl>
                </section>
              )}
            {clusterMembers.length <= 1 && marker.side && details.reason && (
              <section className="unified-chart__trade-tooltip-section unified-chart__trade-tooltip-reason">
                <h5>Why</h5>
                <p>{details.reason}</p>
              </section>
            )}
          </div>,
          document.body,
        )}
    </>
  );
}

function ScatterPoint({ cx, cy, color, seriesId }: any): React.ReactElement | null {
  const [theme] = useThemeMode();
  if (![cx, cy].every(Number.isFinite)) return null;
  return (
    <circle
      cx={cx}
      cy={cy}
      r={4}
      fill={color}
      stroke={theme === 'light' ? '#FFFFFF' : '#08111F'}
      strokeWidth={1.5}
      data-series-point={seriesId}
    />
  );
}

function ReferenceLabel({
  viewBox,
  value,
  color,
  id,
  semanticElementId,
  labelPolicy,
  position = 'end',
  offset = 0,
}: any): React.ReactElement | null {
  const [theme] = useThemeMode();
  const x = Number(viewBox?.x ?? viewBox?.x1);
  const y = Number(viewBox?.y ?? viewBox?.y1) + offset;
  const width = Number(viewBox?.width ?? Number(viewBox?.x2) - Number(viewBox?.x1));
  if (![x, y, width].every(Number.isFinite)) return null;
  const label = String(value);
  const labelWidth = Math.max(42, label.length * 6.5 + 14);
  const rectX = position === 'start' ? x + 6 : x + width - labelWidth - 6;
  return (
    <g
      data-chart-reference-label={id}
      data-chart-label-policy={labelPolicy}
      data-semantic-element-id={semanticElementId}
    >
      <rect
        x={rectX}
        y={y - 15}
        width={labelWidth}
        height={18}
        rx={5}
        fill={theme === 'light' ? '#FFFFFF' : '#08111F'}
        stroke={color}
      />
      <text
        x={rectX + labelWidth / 2}
        y={y - 2}
        fill={theme === 'light' ? '#0F172A' : '#F8FAFC'}
        fontSize={11}
        fontWeight={700}
        textAnchor="middle"
      >
        {label}
      </text>
    </g>
  );
}

function markerValue(spec: ChartSpec, marker: ChartMarker, snappedRow?: PlotRow): number | null {
  if (marker.value !== undefined && Number.isFinite(Number(marker.value))) {
    return Number(marker.value);
  }
  const row = snappedRow || spec.data.find((candidate) => candidate[spec.xAxis.key] === marker.x);
  const series = spec.series.find((candidate) => candidate.id === marker.seriesId);
  return row && series ? seriesValue(row, series) : null;
}

function renderSeries(
  series: ChartSeries,
  index: number,
  rows: PlotRow[],
  plotWidth: number,
  theme: 'dark' | 'light',
): React.ReactNode {
  const color = resolveChartColor(series.style, index, theme);
  const common = {
    yAxisId: series.axisId,
    name: series.label,
    stroke: color,
    fill: series.style?.fill || color,
    opacity: series.style?.opacity ?? 1,
    isAnimationActive: false,
    'data-series-id': series.id,
    'data-semantic-element-id': series.semanticElementId,
  };
  if (series.mark === 'candlestick' && !shouldRenderCandlesticks(plotWidth, rows.length)) {
    const closeKey = series.fieldMapping?.close;
    if (!closeKey) return null;
    return (
      <Line
        key={series.id}
        {...common}
        dataKey={closeKey}
        type="linear"
        connectNulls={false}
        strokeWidth={series.style?.lineWidth || 2}
        dot={false}
        activeDot={{ r: 4 }}
        data-series-mark="line"
        data-series-density="collapsed-candlestick"
      />
    );
  }
  if (series.mark === 'candlestick') {
    const mapping = series.fieldMapping!;
    return (
      <Bar
        key={series.id}
        {...common}
        dataKey={(row: Record<string, unknown>) => [
          Number(row[mapping.low]),
          Number(row[mapping.high]),
        ]}
        shape={(props: any) => <CandlestickShape {...props} series={series} />}
        maxBarSize={22}
      />
    );
  }
  if (series.mark === 'bar') {
    return (
      <Bar
        key={series.id}
        {...common}
        dataKey={series.dataKey!}
        maxBarSize={24}
        radius={[2, 2, 0, 0]}
        data-series-mark="bar"
        data-series-placement={series.placement || 'standard'}
      >
        {rows.map((row, rowIndex) => {
          const color = barColor(series, row, rows[rowIndex - 1]);
          return <Cell key={`${series.id}-${row.__chartIndex}`} fill={color} stroke={color} />;
        })}
      </Bar>
    );
  }
  if (series.mark === 'area')
    return (
      <Area
        key={series.id}
        {...common}
        dataKey={series.dataKey!}
        type={lineType(series.interpolation)}
        strokeWidth={series.style?.lineWidth || 2}
        fillOpacity={series.style?.opacity ?? 0.18}
        data-series-mark="area"
      />
    );
  if (series.mark === 'scatter') {
    return (
      <Line
        key={series.id}
        {...common}
        className="chart-series--scatter"
        dataKey={series.dataKey!}
        type="linear"
        connectNulls={false}
        stroke="none"
        dot={(props: any) => <ScatterPoint {...props} color={color} seriesId={series.id} />}
        activeDot={{ r: 5 }}
        data-series-mark="scatter"
      />
    );
  }
  return (
    <Line
      key={series.id}
      {...common}
      dataKey={series.dataKey!}
      type={lineType(series.interpolation)}
      connectNulls={false}
      strokeWidth={series.style?.lineWidth || 2}
      strokeDasharray={dashArray(series.style?.dash || 'solid')}
      dot={false}
      activeDot={{ r: 4 }}
      data-series-mark="line"
    />
  );
}

export function volumeOverlayDomain(
  series: ChartSeries[],
  rows: ChartRow[],
  axisId: string,
): [number, number] | null {
  const overlays = series.filter(
    (item) =>
      item.axisId === axisId &&
      item.role === 'volume' &&
      item.placement === 'price-volume-overlay' &&
      item.dataKey,
  );
  if (overlays.length === 0) return null;
  const maximum = Math.max(
    0,
    ...overlays.flatMap((item) =>
      rows
        .map((row) => Number(item.dataKey ? row[item.dataKey] : Number.NaN))
        .filter(Number.isFinite),
    ),
  );
  if (maximum <= 0) return [0, 1];
  const ratio = Math.min(...overlays.map((item) => item.overlayHeightRatio ?? 0.24));
  return [0, maximum / ratio];
}

type TimeSeriesPlotProps = {
  syncId: string;
  spec: ChartSpec;
  pane: ChartPane;
  height: number;
  showXAxis: boolean;
  visibleRange: [number, number];
  activeIndex: number;
  hiddenSeries: Set<string>;
  hiddenLayers: Set<string>;
  hiddenOverlayLayers: Set<string>;
  scaleMode: 'linear' | 'log' | 'percentage' | 'indexed';
  onHover: (index: number) => void;
};

type RechartsHoverState = {
  activeLabel?: unknown;
  activeTooltipIndex?: unknown;
  activePayload?: Array<{ payload?: Partial<PlotRow> }>;
};

export function hoveredChartIndex(
  state: RechartsHoverState | null | undefined,
  rows: PlotRow[],
): number | null {
  if (!state || rows.length === 0) return null;

  const activeX = Number(state.activeLabel);
  if (Number.isFinite(activeX)) {
    const nearest = rows.reduce((best, row) =>
      Math.abs(row.__chartX - activeX) < Math.abs(best.__chartX - activeX) ? row : best,
    );
    return nearest.__chartIndex;
  }

  const payloadIndex = Number(
    state.activePayload?.find((entry) => Number.isInteger(Number(entry.payload?.__chartIndex)))
      ?.payload?.__chartIndex,
  );
  if (Number.isInteger(payloadIndex)) return payloadIndex;

  const visibleIndex = Number(state.activeTooltipIndex);
  if (Number.isInteger(visibleIndex) && rows[visibleIndex]) {
    return rows[visibleIndex].__chartIndex;
  }

  return null;
}

export function pointerChartIndex(
  clientX: number,
  left: number,
  width: number,
  rows: Array<Pick<PlotRow, '__chartIndex'> & Partial<Pick<PlotRow, '__chartX'>>>,
): number | null {
  if (
    !Number.isFinite(clientX) ||
    !Number.isFinite(left) ||
    !Number.isFinite(width) ||
    width <= 0 ||
    rows.length === 0
  ) {
    return null;
  }
  const ratio = Math.min(1, Math.max(0, (clientX - left) / width));
  const firstX = rows[0]?.__chartX;
  const lastX = rows.at(-1)?.__chartX;
  if (firstX !== undefined && lastX !== undefined) {
    const padding = Math.max(1, Math.abs(lastX - firstX) * 0.025);
    const chartX = firstX - padding + ratio * (lastX - firstX + padding * 2);
    return rows.reduce((nearest, row) =>
      Math.abs((row.__chartX ?? 0) - chartX) < Math.abs((nearest.__chartX ?? 0) - chartX)
        ? row
        : nearest,
    ).__chartIndex;
  }
  const visibleIndex = Math.round(ratio * (rows.length - 1));
  return rows[visibleIndex]?.__chartIndex ?? null;
}

export function chartXAxisTicks(rows: PlotRow[], maximum = 5): number[] {
  if (rows.length === 0 || maximum <= 0) return [];
  if (maximum === 1) return [rows[0].__chartX];
  if (rows.length <= maximum) return rows.map((row) => row.__chartX);
  const lastIndex = rows.length - 1;
  return Array.from(
    { length: maximum },
    (_, tickIndex) => rows[Math.round((tickIndex * lastIndex) / (maximum - 1))].__chartX,
  );
}

function hoverIndexFromPointer(
  clientX: number,
  element: HTMLDivElement,
  rows: PlotRow[],
  onHover: (index: number) => void,
): void {
  const bounds = element.getBoundingClientRect();
  const svg = element.querySelector('.recharts-surface');
  const clip = svg?.querySelector('clipPath rect');
  let left = bounds.left;
  let width = bounds.width;
  if (svg instanceof SVGSVGElement && clip) {
    const svgBounds = svg.getBoundingClientRect();
    const viewWidth = Number(svg.getAttribute('width'));
    const clipLeft = Number(clip.getAttribute('x'));
    const clipWidth = Number(clip.getAttribute('width'));
    if (viewWidth > 0 && svgBounds.width > 0 && clipWidth > 0) {
      left = svgBounds.left + (clipLeft / viewWidth) * svgBounds.width;
      width = (clipWidth / viewWidth) * svgBounds.width;
    }
  }
  const index = pointerChartIndex(clientX, left, width, rows);
  if (index !== null) onHover(index);
}

function hoverIndexFromSurface(
  clientX: number,
  surface: HTMLDivElement,
  rows: PlotRow[],
  onHover: (index: number) => void,
): void {
  const pane = surface.parentElement;
  if (pane instanceof HTMLDivElement) hoverIndexFromPointer(clientX, pane, rows, onHover);
}

export default function TimeSeriesPlot({
  syncId,
  spec,
  pane,
  height,
  showXAxis,
  visibleRange,
  activeIndex,
  hiddenSeries,
  hiddenLayers,
  hiddenOverlayLayers,
  scaleMode,
  onHover,
}: TimeSeriesPlotProps): React.ReactElement {
  const [theme] = useThemeMode();
  const paneRef = useRef<HTMLDivElement>(null);
  const [plotWidth, setPlotWidth] = useState(FALLBACK_PLOT_WIDTH_PX);
  useLayoutEffect(() => {
    const node = paneRef.current;
    if (!node) return undefined;
    const applyWidth = (width: number) => {
      setPlotWidth(width > 0 ? width : FALLBACK_PLOT_WIDTH_PX);
    };
    applyWidth(node.clientWidth);
    if (!('ResizeObserver' in globalThis)) return undefined;
    const observer = new globalThis.ResizeObserver((entries) => {
      applyWidth(entries[0]?.contentRect.width || 0);
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  const allRows = useMemo(() => plottedRows(spec), [spec]);
  const markerPositioner = useMemo(
    () => createMarkerPositioner(allRows, spec.xAxis.key, spec.xAxis.type),
    [allRows, spec.xAxis.key, spec.xAxis.type],
  );
  const visibleRows = allRows.slice(visibleRange[0], visibleRange[1] + 1);
  const paneSeries = spec.series.filter((item) => item.paneId === pane.id);
  const rows = bucketPlotRows(
    visibleRows,
    targetDisplayPointCount(plotWidth),
    seriesAggregations(paneSeries),
  );
  const axes = spec.axes.filter((axis) => pane.axisIds.includes(axis.id));
  const layerVisible = (kind: string, item: { id: string; layerId?: string; groupId?: string }) => {
    const layerId = chartObjectLayerId(kind, item);
    return !hiddenLayers.has(layerId) && (kind === 'series' || !hiddenOverlayLayers.has(layerId));
  };
  const series = paneSeries
    .filter((item) => !hiddenSeries.has(item.id) && layerVisible('series', item))
    .sort(
      (left, right) =>
        Number(right.placement === 'price-volume-overlay') -
        Number(left.placement === 'price-volume-overlay'),
    );
  const references = (spec.referenceLines || []).filter(
    (line) => line.paneId === pane.id && layerVisible('line', line),
  );
  const areas = chartAreas(spec).filter(
    (area) => area.paneId === pane.id && layerVisible('area', area),
  );
  const markers = (spec.markers || []).filter(
    (marker) =>
      // Buy and Sell are ordinary marker layers, so one layer check covers them
      // exactly as it covers a series or an annotation.
      marker.paneId === pane.id && layerVisible('marker', marker),
  );
  const displayedMarkers = clusterVisibleMarkers(markers, markerPositioner, {
    widthPx: plotWidth,
    minX: allRows[visibleRange[0]]?.__chartX ?? 0,
    maxX: allRows[visibleRange[1]]?.__chartX ?? 0,
  });
  const activeRow = displayedRowForIndex(rows, activeIndex);
  const activeStartIndex = activeRow
    ? Number(activeRow.__chartBucketStartIndex ?? activeRow.__chartIndex)
    : undefined;
  const activeEndIndex = activeRow
    ? Number(activeRow.__chartBucketEndIndex ?? activeRow.__chartIndex)
    : undefined;
  const timeRange = chartTimeRange(spec);
  const activeDateLabel =
    activeStartIndex !== undefined && activeEndIndex !== undefined
      ? activeStartIndex === activeEndIndex
        ? formatX(spec, allRows[activeEndIndex], timeRange, true)
        : `${formatX(spec, allRows[activeStartIndex], timeRange, true)} to ${formatX(
            spec,
            allRows[activeEndIndex],
            timeRange,
            true,
          )}`
      : '';
  const xByValue = new Map(allRows.map((row) => [row[spec.xAxis.key], row.__chartX]));
  const firstVisibleX = rows[0]?.__chartX ?? 0;
  const lastVisibleX = rows.at(-1)?.__chartX ?? 1;
  const xPadding = Math.max(1, Math.abs(lastVisibleX - firstVisibleX) * 0.025);
  const background =
    pane.appearance?.backgroundColor ||
    (theme === 'light' ? LIGHT_TONE_BACKGROUNDS : TONE_BACKGROUNDS)[
      pane.appearance?.tone || 'neutral'
    ];
  const referenceValues = references.map((line) => line.value);
  const referenceRange = Math.max(...referenceValues, 1) - Math.min(...referenceValues, 0);
  // Reserve two 18px label rows plus breathing room. Using the rendered plot
  // height keeps collision detection stable across compact mobile and expanded
  // desktop charts instead of comparing raw price deltas.
  const labelOffsets = referenceLabelOffsets(
    referenceValues,
    Math.max(0.001, (referenceRange * 42) / Math.max(100, height - 60)),
  );
  const hasVolumeOverlay = series.some((item) => item.placement === 'price-volume-overlay');
  const unavailableLogAxes =
    scaleMode === 'log'
      ? axes.filter((axis) => {
          const isOverlayAxis = paneSeries.some(
            (item) => item.axisId === axis.id && item.placement === 'price-volume-overlay',
          );
          return !isOverlayAxis && positiveLogDomain(spec, axis.id, visibleRange) === null;
        })
      : [];
  const chartTopMargin = paneSeries.length > 4 ? 58 : 38;
  const representedKeys = new Set<string>([spec.xAxis.key]);
  paneSeries.forEach((item) => {
    if (item.dataKey) representedKeys.add(item.dataKey);
    Object.values(item.fieldMapping || {}).forEach((key) => representedKeys.add(key));
  });

  return (
    <div
      ref={paneRef}
      className="unified-chart__pane"
      data-testid={`chart-pane-${pane.id}`}
      data-pane-id={pane.id}
      data-pane-tone={pane.appearance?.tone || 'neutral'}
      data-has-volume-overlay={hasVolumeOverlay ? 'true' : 'false'}
      data-source-point-count={visibleRows.length}
      data-display-point-count={rows.length}
      data-event-window-ids={areas
        .filter((area) => area.xStart !== undefined)
        .map((area) => area.id)
        .join(',')}
      data-area-ids={areas.map((area) => area.id).join(',')}
      onMouseMove={(event) =>
        hoverIndexFromPointer(event.clientX, event.currentTarget, rows, onHover)
      }
      onPointerMove={(event) =>
        hoverIndexFromPointer(event.clientX, event.currentTarget, rows, onHover)
      }
      style={{ height, backgroundColor: background }}
    >
      {unavailableLogAxes.length > 0 && (
        <div className="unified-chart__scale-notice" role="status">
          Logarithmic scale needs positive values on this axis. Showing a valid linear view.
        </div>
      )}
      <div
        className="unified-chart__pane-readout"
        data-testid={`chart-readout-${pane.id}`}
        data-active-index={activeIndex}
        data-displayed-start-index={activeStartIndex}
        data-displayed-end-index={activeEndIndex}
      >
        <strong>{paneDisplayLabel(pane)}</strong>
        <span>{activeDateLabel}</span>
        {series.flatMap((item) => {
          if (item.mark === 'candlestick' && item.fieldMapping) {
            const axisFormat = axes.find((axis) => axis.id === item.axisId)?.format || 'number';
            return (['open', 'high', 'low', 'close'] as const).map((field) => (
              <span key={`${item.id}-${field}`}>
                <b>{field[0].toUpperCase()}</b>{' '}
                {formatNumber(activeRow?.[item.fieldMapping![field]], axisFormat)}
              </span>
            ));
          }
          const value = seriesValue(activeRow, item);
          return value === null
            ? []
            : [
                <span
                  key={item.id}
                  style={{ color: resolveChartColor(item.style, paneSeries.indexOf(item), theme) }}
                >
                  <b>{item.label}</b>{' '}
                  {formatNumber(
                    value,
                    axes.find((axis) => axis.id === item.axisId)?.format || 'number',
                  )}
                </span>,
              ];
        })}
        {(spec.hoverFields || [])
          .filter((field) => field.paneId === pane.id && !representedKeys.has(field.dataKey))
          .map((field) => (
            <span key={field.id} style={{ color: field.color }}>
              <b>{field.label}</b> {formatHoverValue(activeRow?.[field.dataKey], field.format)}
            </span>
          ))}
        {markers
          .filter((marker) => !marker.side)
          .filter((marker) => {
            const markerTime = chartTimeMilliseconds(marker.x);
            const rowTime = chartTimeMilliseconds(activeRow?.[spec.xAxis.key]);
            return markerTime !== null && rowTime !== null && markerTime === rowTime;
          })
          .map((marker) => (
            <span key={marker.id} data-testid={`chart-marker-readout-${marker.id}`}>
              <b>{marker.label}</b>
              {marker.detail && marker.detail !== marker.label ? ` ${marker.detail}` : null}
            </span>
          ))}
      </div>
      <div
        aria-hidden="true"
        className="unified-chart__hover-surface"
        data-testid={`chart-hover-${pane.id}`}
        onMouseMove={(event) =>
          hoverIndexFromSurface(event.clientX, event.currentTarget, rows, onHover)
        }
        onPointerMove={(event) =>
          hoverIndexFromSurface(event.clientX, event.currentTarget, rows, onHover)
        }
      />
      <ResponsiveContainer width="100%" height="100%">
        <ComposedChart
          data={rows}
          margin={{ top: chartTopMargin, right: 8, bottom: showXAxis ? 5 : 0, left: 0 }}
          syncId={syncId}
          syncMethod="value"
        >
          <CartesianGrid
            stroke={theme === 'light' ? 'rgba(71, 85, 105, 0.14)' : 'rgba(100, 116, 139, 0.16)'}
            strokeDasharray="3 5"
            vertical={false}
          />
          <XAxis
            dataKey="__chartX"
            style={{ pointerEvents: 'none' }}
            type="number"
            domain={[firstVisibleX - xPadding, lastVisibleX + xPadding]}
            hide={!showXAxis}
            interval="preserveStartEnd"
            tickFormatter={(value) =>
              formatX(
                spec,
                allRows.find((row) => row.__chartX === value),
                timeRange,
              )
            }
            ticks={chartXAxisTicks(rows)}
            tick={{ fill: theme === 'light' ? '#64748B' : '#8190A5', fontSize: 10 }}
            axisLine={{ stroke: theme === 'light' ? '#CBD5E1' : '#27344A' }}
            tickLine={false}
            minTickGap={28}
          />
          {axes.map((axis) => {
            const isOverlayAxis = paneSeries.some(
              (item) => item.axisId === axis.id && item.placement === 'price-volume-overlay',
            );
            const overlayDomain = volumeOverlayDomain(series, rows, axis.id);
            const logDomain =
              !isOverlayAxis && scaleMode === 'log'
                ? positiveLogDomain(spec, axis.id, visibleRange)
                : null;
            return (
              <YAxis
                key={axis.id}
                yAxisId={axis.id}
                orientation={axis.side}
                scale={logDomain || (!isOverlayAxis && axis.scale === 'log') ? 'log' : 'auto'}
                domain={
                  overlayDomain ||
                  logDomain ||
                  (isOverlayAxis
                    ? [0, 1]
                    : [
                        axis.domain?.min ?? (axis.domain?.includeZero ? 0 : 'auto'),
                        axis.domain?.max ?? 'auto',
                      ])
                }
                hide={isOverlayAxis}
                tickFormatter={(value) => formatNumber(value, axis.format)}
                tick={{ fill: theme === 'light' ? '#64748B' : '#8190A5', fontSize: 10 }}
                axisLine={false}
                tickLine={false}
                width={isOverlayAxis ? 0 : 56}
                mirror
              />
            );
          })}
          <Tooltip
            content={() => null}
            cursor={{ stroke: theme === 'light' ? '#64748B' : '#94A3B8', strokeWidth: 1 }}
          />
          {areas.map((area) => (
            <ReferenceArea
              key={area.id}
              className={`chart-area chart-area--${area.id}`}
              data-chart-area-id={area.id}
              x1={area.xStart === undefined ? undefined : xByValue.get(area.xStart)}
              x2={area.xEnd === undefined ? undefined : xByValue.get(area.xEnd)}
              yAxisId={area.axisId || pane.axisIds[0]}
              y1={area.yStart}
              y2={area.yEnd}
              fill={area.style?.fill || area.style?.color || '#64748B'}
              fillOpacity={area.style?.opacity ?? 0.14}
              style={{ pointerEvents: 'none' }}
              ifOverflow={area.yStart !== undefined ? 'extendDomain' : 'hidden'}
            />
          ))}
          {series.map((item, index) => renderSeries(item, index, rows, plotWidth, theme))}
          {references.map((line, lineIndex) => {
            const segment:
              | readonly [{ x?: number; y: number }, { x?: number; y: number }]
              | undefined =
              line.xStart !== undefined && line.xEnd !== undefined
                ? [
                    { x: xByValue.get(line.xStart), y: line.value },
                    { x: xByValue.get(line.xEnd), y: line.value },
                  ]
                : undefined;
            const lineColor = resolveChartColor(line.style, lineIndex, theme);
            const layerId = chartObjectLayerId('line', line);
            const labelPolicy =
              line.labelPolicy || spec.layers?.find((layer) => layer.id === layerId)?.labelPolicy;
            return (
              <ReferenceLine
                key={line.id}
                className={`chart-layer--${layerId}`}
                data-chart-line-id={line.id}
                yAxisId={line.axisId}
                {...(segment ? { segment } : { y: line.value })}
                stroke={lineColor}
                strokeWidth={line.style?.lineWidth || 1}
                strokeOpacity={line.style?.opacity ?? 1}
                strokeDasharray={dashArray(line.style?.dash || 'dashed')}
                ifOverflow="extendDomain"
                label={{
                  value: line.label,
                  content: (props: any) => (
                    <ReferenceLabel
                      {...props}
                      id={line.id}
                      semanticElementId={line.semanticElementId}
                      labelPolicy={labelPolicy}
                      color={lineColor}
                      position={line.labelPosition || 'end'}
                      offset={labelOffsets[lineIndex]}
                    />
                  ),
                }}
              />
            );
          })}
          {displayedMarkers.map((marker) => {
            const anchorRow = allRows.find((row) => row.__chartX >= marker.chartX);
            const value = markerValue(spec, marker, anchorRow);
            const targetSeries = spec.series.find((candidate) => candidate.id === marker.seriesId);
            const x = marker.chartX;
            if (value === null || !targetSeries || x === undefined) return null;
            const collidesWithReference = references.some(
              (line) =>
                line.axisId === targetSeries.axisId &&
                Math.abs(line.value - value) <=
                  Math.max(0.001, (referenceRange * 20) / Math.max(100, height - 60)),
            );
            const layerId = chartObjectLayerId('marker', marker);
            const labelPolicy =
              marker.labelPolicy ||
              spec.layers?.find((layer) => layer.id === layerId)?.labelPolicy ||
              'auto';
            return (
              <ReferenceDot
                key={marker.id}
                x={x}
                y={value}
                yAxisId={targetSeries.axisId}
                ifOverflow="visible"
                shape={(props: any) => (
                  <MarkerGlyph
                    {...props}
                    marker={marker}
                    labelPolicy={labelPolicy}
                    compact={marker.compact || collidesWithReference || labelPolicy !== 'always'}
                  />
                )}
              />
            );
          })}
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}
